import { useTranslation } from 'next-i18next'
import React from 'react'

interface EvaluationCTAProps {
  onStartEvaluation: () => void
}

export default function EvaluationCTA({
  onStartEvaluation,
}: EvaluationCTAProps) {
  const { t } = useTranslation('avaliacao360')

  return (
    <div className="bg-gradient-to-br from-red-900/40 via-red-800/30 to-slate-900/80 rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-red-500/20 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-red-600/10 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:h-6 sm:w-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white">
                  {t('cta.title', 'Comece a Avaliar!')}
                </h3>
                <p className="text-amber-200 text-sm font-medium">
                  {t('cta.subtitle', 'Ajude seus parceiros a evoluir')}
                </p>
              </div>
            </div>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-xl">
              {t(
                'cta.description',
                'Avalie seus parceiros de treino e contribua para a evolução de todos. Quanto mais avaliações você fizer, mais preciso será o seu próprio card!',
              )}
            </p>
          </div>
        </div>

        {/* How it works */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
          <StepCard
            number={1}
            title={t('cta.step1.title', 'Escolha um parceiro')}
            description={t(
              'cta.step1.description',
              'Selecione um colega da sua equipe para avaliar',
            )}
          />
          <StepCard
            number={2}
            title={t('cta.step2.title', 'Avalie as habilidades')}
            description={t(
              'cta.step2.description',
              'Distribua pontos nas características e habilidades',
            )}
          />
          <StepCard
            number={3}
            title={t('cta.step3.title', 'Veja o resultado')}
            description={t(
              'cta.step3.description',
              'Os cards são gerados com base nas avaliações',
            )}
          />
        </div>

        {/* CTA Button */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={onStartEvaluation}
            className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white px-8 py-4 rounded-xl transition-all duration-300 shadow-lg shadow-red-500/30 hover:shadow-red-500/50 font-bold text-base sm:text-lg hover:-translate-y-0.5"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
            {t('cta.button', 'Iniciar Avaliação')}
          </button>
          <p className="text-slate-400 text-xs sm:text-sm">
            {t('cta.hint', 'Leva apenas alguns minutos!')}
          </p>
        </div>
      </div>
    </div>
  )
}

// Step Card Component
interface StepCardProps {
  number: number
  title: string
  description: string
}

function StepCard({ number, title, description }: StepCardProps) {
  return (
    <div className="flex items-start gap-3 p-3 sm:p-4 bg-white/5 rounded-xl border border-white/5">
      <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
        <span className="text-red-400 font-bold text-sm">{number}</span>
      </div>
      <div>
        <h4 className="text-white font-semibold text-sm mb-1">{title}</h4>
        <p className="text-slate-400 text-xs leading-relaxed">{description}</p>
      </div>
    </div>
  )
}
